import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Navigation, Footer, WhatsAppButton, PageHeader, WHATSAPP_LINK } from "@/components/Layout";
import { Mail, MapPin, Linkedin, MessageCircle } from "lucide-react";

export default function ContactPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="contact-page" className="min-h-screen">
      <Navigation isScrolled={true} forceDark={true} />
      
      <PageHeader 
        badge="Contact Us"
        title="Get in Touch"
        subtitle="Ready to navigate complexity? Let's start a conversation."
      />

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Let's work through the <span className="text-gradient">complexity together</span></h2>
              <p className="text-slate-600 text-lg mb-10 leading-relaxed">Whether you're facing a strategic challenge, exploring new opportunities, or looking to transform your organisation, we'd love to hear from you.</p>

              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="service-icon-container w-12 h-12 flex-shrink-0"><Mail className="text-cyan-500" size={24} strokeWidth={1.5} /></div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                    <a href="mailto:hello@infinium-innovations.com" className="text-slate-600 hover:text-cyan-600 transition-colors">hello@infinium-innovations.com</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="service-icon-container w-12 h-12 flex-shrink-0"><MapPin className="text-cyan-500" size={24} strokeWidth={1.5} /></div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Locations</h3>
                    <p className="text-slate-600">Johannesburg, South Africa</p>
                    <p className="text-slate-600">Toronto, Canada</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="service-icon-container w-12 h-12 flex-shrink-0"><Linkedin className="text-cyan-500" size={24} strokeWidth={1.5} /></div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">LinkedIn</h3>
                    <a href="https://www.linkedin.com/company/111719189" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-cyan-600 transition-colors">Follow us on LinkedIn</a>
                  </div>
                </div>
              </div>

              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 btn-gradient text-white rounded-full px-8 py-4 text-lg font-medium hover:scale-105 transition-transform">
                <MessageCircle size={24} />
                Start a WhatsApp Conversation
              </a>
            </div>

            <div>
              <Card className="bg-slate-50 border-slate-100 rounded-2xl">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold text-slate-900 mb-6">Send us a message</h3>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                      <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none transition-all" placeholder="Your name" />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                      <input type="email" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none transition-all" placeholder="your@email.com" />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Company</label>
                      <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none transition-all" placeholder="Your company" />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                      <textarea rows={4} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none transition-all resize-none" placeholder="Tell us about your project or challenge..." />
                    </div>
                    
                    <Button className="w-full btn-gradient text-white rounded-xl py-3 font-medium">Send Message</Button>
                    
                    <p className="text-slate-400 text-sm text-center">Or reach out directly via WhatsApp for a faster response.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
